import TelemetryButton from "@/design-system/telemetry-button";
import * as stylex from "@stylexjs/stylex";

import { type PropsWithStyleX, genericStyles } from "../../../styles/tokens.stylex";

const styles = stylex.create({
    container: (borderColor: string) => ({
        fill: { default: null, ":hover": borderColor },
        padding: 0,
        borderColor: { default: "transparent", ":hover": borderColor },
        borderRadius: genericStyles.borderRadiusFullRound,
        borderStyle: "solid",
        borderWidth: "3px",
        transition: "border 0.3s",
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        height: "3.125rem",
        width: "3.125rem",
    }),
    svg: {
        transition: "fill 0.3s",
        width: "1.6rem",
    },
});

const socialSvgPaths = {
    facebook: {
        path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
        border: "#1877f2",
    },
    linkedin: {
        path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
        border: "#0a66c2",
    },
    youtube: {
        path: "M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
        border: "#ff0000",
    },
    instagram: {
        path: "M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913a5.885 5.885 0 001.384 2.126A5.868 5.868 0 004.14 23.37c.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558a5.898 5.898 0 002.126-1.384 5.86 5.86 0 001.384-2.126c.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913a5.89 5.89 0 00-1.384-2.126A5.847 5.847 0 0019.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227a3.81 3.81 0 01-.899 1.382 3.744 3.744 0 01-1.38.896c-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421a3.716 3.716 0 01-1.379-.899 3.644 3.644 0 01-.9-1.38c-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 01-2.88 0 1.44 1.44 0 012.88 0z",
        border: "#9b36b7",
    },
    millennium: {
        path: "M18.6 0H5.4C2.4 0 0 2.4 0 5.4v13.2c0 3 2.4 5.4 5.4 5.4h13.2c3 0 5.4-2.4 5.4-5.4V5.4c0-3-2.4-5.4-5.4-5.4zm-1.2 18.7s0-.7-.1-1.3l-1-8.2-3.8 9.7c-.8-.6-1.1-1.1-1.5-2.1L7.8 9.2l-1 7.3c-.1 1-.2 2.2-.2 2.2H5.2s.3-1.5.4-2.4l1-7.5c.2-1.7-.3-3-1.1-3.5-.6-.5-1.7-.5-1.7-.5s1.4-.1 1.9.1c1.2.2 2.6 1.1 3.5 3.4l3.2 7.5 2.8-7.2c.1-.4.3-.7.3-1.1h2.3s0 .7.1 1.1l1.1 8.3c.1.8.3 1.8.3 1.8h-1.9z",
        border: "#bd004f",
    },
};

type ISocialMediaLink = PropsWithStyleX & {
    socialPlatform: keyof typeof socialSvgPaths;
    trackerIndex: string;
    href: string;
};

export default function SocialMediaLink({
    socialPlatform,
    styleX: style,
    trackerIndex,
    ...props
}: ISocialMediaLink) {
    const { border, path } = socialSvgPaths[socialPlatform];

    return (
        <TelemetryButton
            {...props}
            as="a"
            size="M"
            styleX={[styles.container(border), style]}
            param1={trackerIndex}
            eventName="homepg-footerSocialClick"
        >
            <svg {...stylex.props(styles.svg)} viewBox="0 0 24 24">
                <path d={path} />
            </svg>
        </TelemetryButton>
    );
}
